<template>
	<div>
		<SgnlTable
			:columns="columns"
			:data="keysToList"
			:initial-sorting="[{ id: 'FormattedCreatedAt', desc: true }]"
			:loading="keyListLoading"
			:empty-text="noDataText"
			empty-body="Generate a new API key above to authenticate your programmatic requests to the Signaloid Cloud Compute Engine."
			:empty-icon="emptyIcon"
			:card-breakpoint="959"
			paginated
			:page-size="10"
			:page-size-options="[10, 25, 50, 100]"
			:error="loadError"
			error-title="Could not load your API keys."
			:error-message="errorMessage"
			:retrying="keyListLoading"
			@retry="refreshList"
		>
			<!-- Name -->
			<template #cell-Name="{ row }">
				<div
					class="key-name sgnl-flex sgnl-align-center"
					:title="row.Name"
					data-test="keys-list-key-entry"
					:data-test-value="row.KeyID"
				>
					<span class="sgnl-paragraph-s-regular sgnl-text-color-default">{{ row.Name }}</span>
				</div>
			</template>

			<!-- Owner -->
			<template #cell-owner="{ row }">
				<v-tooltip
					v-if="row.OrganizationID"
					location="bottom"
				>
					<template v-slot:activator="{ props }">
						<span
							v-bind="props"
							class="key-owner sgnl-flex sgnl-align-center sgnl-gap-8 sgnl-paragraph-s-regular sgnl-text-color-secondary"
						>
							<Enterprise16 class="sgnl-icon-color-secondary sgnl-flex-shrink-0" />
							Organization
						</span>
					</template>
					<span>
						<strong>Organization Key</strong><br />
						This key was created as part of an organization.<br />
						It will become inactive if you are removed from the organization.
					</span>
				</v-tooltip>
				<span
					v-else
					class="key-owner sgnl-flex sgnl-align-center sgnl-gap-8 sgnl-paragraph-s-regular sgnl-text-color-secondary"
				>
					<User16 class="sgnl-icon-color-secondary sgnl-flex-shrink-0" />
					You
				</span>
			</template>

			<!-- Key -->
			<template #cell-FormattedKeyID="{ row }">
				<span class="sgnl-code-default sgnl-text-color-secondary">{{ row.FormattedKeyID }}</span
				><span class="sgnl-code-default sgnl-text-color-secondary">...</span>
			</template>

			<!-- Creation Date -->
			<template #cell-FormattedCreatedAt="{ row }">
				<div class="sgnl-flex sgnl-align-center sgnl-gap-8">
					<span class="sgnl-paragraph-s-regular sgnl-text-color-secondary">{{ row.FormattedCreatedAt }}</span>
					<v-tooltip
						location="bottom"
						v-if="daysDiff(row.CreatedAt) <= -90"
					>
						<template v-slot:activator="{ props }">
							<Warning16
								class="sgnl-icon-color-warning"
								v-bind="props"
							/>
						</template>
						<span>This key is {{ -daysDiff(row.CreatedAt) }} days old.</span>
					</v-tooltip>
				</div>
			</template>

			<!-- Valid Until -->
			<template #cell-FormattedValidUntil="{ row }">
				<div
					v-if="isExpired(row)"
					class="sgnl-flex sgnl-align-center sgnl-gap-4 sgnl-paragraph-s-regular sgnl-text-color-secondary"
				>
					<DotMark16 class="sgnl-flex-shrink-0 sgnl-icon-color-error" />
					<span>Expired</span>
				</div>
				<div
					v-else
					class="sgnl-flex sgnl-align-center sgnl-gap-8"
				>
					<span class="sgnl-paragraph-s-regular sgnl-text-color-secondary">{{
						row.FormattedValidUntil
					}}</span>
					<v-tooltip
						location="bottom"
						v-if="daysDiff(row.ValidUntil) < 7"
					>
						<template v-slot:activator="{ props }">
							<Warning16
								class="sgnl-icon-color-warning"
								v-bind="props"
							/>
						</template>
						<span
							>This key will expire in {{ daysDiff(row.ValidUntil) }} day{{
								daysDiff(row.ValidUntil) == 1 ? "" : "s"
							}}.</span
						>
					</v-tooltip>
				</div>
			</template>

			<!-- Actions -->
			<template #cell-actions="{ row }">
				<div class="actions-cell">
					<v-btn
						icon
						variant="text"
						size="small"
						@click="handleDeleteKey(row)"
						data-test="keys-list-delete-key-button"
						class="delete-btn"
					>
						<TrashCan16 class="sgnl-icon-color-error" />
						<v-tooltip
							activator="parent"
							location="bottom"
							>Delete API key</v-tooltip
						>
					</v-btn>
				</div>
			</template>

			<!-- Mobile card -->
			<template #card="{ row }">
				<div class="key-card__top">
					<div class="key-card__name sgnl-flex sgnl-align-center sgnl-gap-8">
						<span class="key-card__name-text sgnl-paragraph-s-medium sgnl-text-color-default">{{
							row.Name
						}}</span>
						<span
							class="key-owner sgnl-flex sgnl-align-center sgnl-gap-8 sgnl-flex-shrink-0 sgnl-paragraph-s-regular sgnl-text-color-secondary"
						>
							<Enterprise16
								v-if="row.OrganizationID"
								class="sgnl-icon-color-secondary sgnl-flex-shrink-0"
							/>
							<User16
								v-else
								class="sgnl-icon-color-secondary sgnl-flex-shrink-0"
							/>
							{{ row.OrganizationID ? "Organization" : "You" }}
						</span>
					</div>
					<v-btn
						icon
						variant="text"
						size="small"
						class="delete-btn"
						@click="handleDeleteKey(row)"
					>
						<TrashCan16 class="sgnl-icon-color-error" />
						<v-tooltip
							activator="parent"
							location="bottom"
							>Delete API key</v-tooltip
						>
					</v-btn>
				</div>
				<div class="key-card__id sgnl-code-default sgnl-text-color-secondary">{{ row.FormattedKeyID }}...</div>
				<div
					class="key-card__meta sgnl-flex sgnl-align-center sgnl-gap-8 sgnl-paragraph-s-regular sgnl-text-color-secondary"
				>
					<span>Created {{ row.FormattedCreatedAt }}</span>
					<span>·</span>
					<span
						v-if="isExpired(row)"
						class="sgnl-flex sgnl-align-center sgnl-gap-4"
					>
						<DotMark16 class="sgnl-flex-shrink-0 sgnl-icon-color-error" />
						<span>Expired</span>
					</span>
					<span v-else>Valid until {{ row.FormattedValidUntil }}</span>
				</div>
			</template>
		</SgnlTable>

		<DeleteAPIKey
			v-if="deleteKeyDialog"
			v-model:show-dialog="deleteKeyDialog"
			@keyDeleted="handleKeyDeleted"
			:passedKey="deleteKeyDialogConfig ?? undefined"
		/>

		<v-snackbar
			v-model="successSnackbar"
			:timeout="3000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			API key deleted successfully.
		</v-snackbar>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Enterprise16 from "@carbon/icons-vue/es/enterprise/16";
import User16 from "@carbon/icons-vue/es/user/16";
import Warning16 from "@carbon/icons-vue/es/warning/16";
import DotMark16 from "@carbon/icons-vue/es/dot-mark/16";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";

// Types
import { AWSAuthenticatedUserObject } from "@/types/user";
import { UserKeyFormatted, UserKey } from "@/types/api/keys";

// Libraries
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import { formatCreatedAt, formatValidUntil } from "@/js/dateUtils";
import moment from "moment";

// Utilities
import * as Sentry from "@sentry/vue";

// Components
import DeleteAPIKey from "@/components/Settings/ComputeEngineApi/DeleteAPIKey.vue";
import SgnlTable from "@/components/Common/Table.vue";
import { type ColumnDef } from "@tanstack/vue-table";

// Assets
import keyIcon from "@/assets/icons/home/key-lifecycle.svg";

// Stores
import { mapStores } from "pinia";
import { useUserStore } from "@/stores/user";
import axios from "axios";
import { isAxiosTimeoutError } from "@/plugins/monitoring";

function formatKeyDates(key: UserKey): UserKeyFormatted {
	return {
		...key,
		FormattedCreatedAt: formatCreatedAt(key.CreatedAt),
		FormattedValidUntil: formatValidUntil(key.ValidUntil),
	};
}

function formatKeyIDs(key: UserKeyFormatted): UserKeyFormatted {
	return {
		...key,
		FormattedKeyID: "scce_" + key.KeyID.substring(0, 8), //+ "…", //"...",
	};
}

// Name, Creation Date and Valid Until are sortable. The date columns sort by their
// numeric timestamps (not the formatted strings); Valid Until ascends first so
// expired/soonest-to-expire keys surface at the top (null = never expires sorts last).
const KEY_COLUMNS: ColumnDef<UserKeyFormatted>[] = [
	{ accessorKey: "Name", header: "Name", enableSorting: true },
	{ id: "owner", header: "Owner", enableSorting: false },
	{ accessorKey: "FormattedKeyID", header: "Key", enableSorting: false },
	{
		id: "FormattedCreatedAt",
		header: "Creation Date",
		accessorFn: (key) => key.CreatedAt ?? 0,
		sortingFn: "basic",
		sortDescFirst: true,
		enableSorting: true,
	},
	{
		id: "FormattedValidUntil",
		header: "Valid Until",
		accessorFn: (key) => key.ValidUntil ?? Infinity,
		sortingFn: "basic",
		sortDescFirst: false,
		enableSorting: true,
	},
	{ id: "actions", header: "Actions", enableSorting: false },
];

type ComponentData = {
	authenticatedUser: undefined | null | AWSAuthenticatedUserObject;
	keysToList: UserKeyFormatted[];
	keyListLoading: boolean;
	deleteKeyDialog: boolean;
	deleteKeyDialogConfig: UserKeyFormatted | null;
	noDataText: string;
	loadError: boolean;
	errorMessage: string;
	successSnackbar: boolean;
};

export default defineComponent({
	name: "ListAPIKey",
	props: {},
	emits: ["count-changed"],
	components: { DeleteAPIKey, SgnlTable, Enterprise16, User16, Warning16, DotMark16, TrashCan16, CheckmarkFilled16 },
	data: (): ComponentData => ({
		authenticatedUser: undefined,
		keysToList: [],
		keyListLoading: false,
		deleteKeyDialog: false,
		deleteKeyDialogConfig: null,
		noDataText: "You have not created any API keys yet.",
		loadError: false,
		errorMessage: "",
		successSnackbar: false,
	}),
	watch: {
		keysToList: {
			handler(keys: UserKeyFormatted[]) {
				this.$emit("count-changed", keys.length);
			},
			immediate: true,
		},
	},
	computed: {
		...mapStores(useUserStore),
		columns(): ColumnDef<UserKeyFormatted>[] {
			return KEY_COLUMNS;
		},
		emptyIcon(): string {
			return keyIcon;
		},
	},
	methods: {
		async refreshList() {
			this.keyListLoading = true;
			this.loadError = false;
			try {
				const keysResponse = await sdk.keys.list();
				this.keysToList = keysResponse.Keys.map(formatKeyDates).map(formatKeyIDs);
			} catch (error) {
				// TODO: Add monitoring
				let severityLevel: Sentry.SeverityLevel = "error";
				if (error instanceof SdkError) {
					/*
					 *	This API can only return: 401, 404 (UserID), 500
					 */
					switch (error.code) {
						case ERROR_CODES.API_SERVER_ERROR:
							severityLevel = "fatal";
							this.errorMessage = `Our system encountered a server error retrieving the API keys.`;
							break;
						case ERROR_CODES.API_UNAUTHORIZED:
							this.errorMessage =
								"Our system encountered an authorization error while retrieving the API keys." +
								" Signing out and back in will ensure you are properly authorized.";
							break;
						case ERROR_CODES.API_NOT_FOUND:
							severityLevel = "fatal";
							this.errorMessage =
								"Our system encountered an error while locating your API keys." +
								" Our team will receive a high priority notification for this issue.";
							break;

						default:
							this.errorMessage = `Our system encountered an unexpected error (${error.code}).`;
							break;
					}
				} else if (axios.isAxiosError(error)) {
					this.errorMessage = "Often this is a temporary network or connectivity issue.";
					if (isAxiosTimeoutError(error)) {
						severityLevel = "info";
					}
				} else {
					// other error
					this.errorMessage = "The network request could not be made due to an unexpected error.";
				}
				this.loadError = true;
				Sentry.captureException(error, {
					level: severityLevel,
					extra: { error: error, userMessage: this.errorMessage },
					tags: { service: "create-task" },
				});
			} finally {
				this.keyListLoading = false;
			}
		},
		handleDeleteKey(item: UserKeyFormatted) {
			this.deleteKeyDialog = true;
			this.deleteKeyDialogConfig = item;
		},
		async handleKeyDeleted() {
			await this.refreshList();
			this.successSnackbar = true;
		},
		isExpired(item: UserKeyFormatted): boolean {
			return this.daysDiff(item.ValidUntil) < 0;
		},
		daysDiff(ts: number | null): number {
			if (ts === null) {
				return Infinity;
			}
			return moment.unix(ts).diff(moment(moment.now()), "days");
		},
	},
	async created() {
		const authUser = await this.userStore.getCurrentAuthenticatedUserObject();
		this.authenticatedUser = authUser;

		await this.refreshList();
	},
});
</script>

<style lang="css" scoped>
:deep(.col-Name) {
	width: 24%;
}

:deep(.col-owner) {
	width: 12%;
}

:deep(.col-FormattedKeyID) {
	width: 18%;
}

:deep(.col-FormattedCreatedAt) {
	width: 18%;
}

:deep(.col-FormattedValidUntil) {
	width: 18%;
}

:deep(.col-actions) {
	width: 10%;
}

.key-owner {
	white-space: nowrap;
}

.key-name {
	gap: var(--sgnl-space-8);
}

.key-name > span {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	min-width: 0;
}

.actions-cell {
	display: flex;
	align-items: center;
}

/* === Mobile card sub-layout === */
.key-card__top {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: var(--sgnl-space-8);
}

.key-card__name {
	min-width: 0;
	flex: 1;
}

.key-card__name-text {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
}

.key-card__meta {
	flex-wrap: wrap;
}
</style>
